/**
 * Nova — Orbit's mascot, animated.
 *
 * A small, friendly retro-futurist robot. She notices, you decide.
 *
 * Spec: see ORBIT_DESIGN.md §7 + the Nova Character Bible (HTML in
 * /Users/rennan/Orbit Framework/).
 *
 * Ten emotional states (see {@link NovaState}). Each has its own CSS
 * keyframe animation — body float / sway / bounce, eye blinks, antenna
 * effects — ported from the bible so iOS and web stay visually in sync.
 *
 * Components that just want a static Nova can pass `animated={false}`.
 * Default is animated.
 */

import { cn } from "@/lib/utils";

export type NovaState =
  | "idle"
  | "curious"
  | "thinking"
  | "has-suggestion"
  | "happy"
  | "sleepy"
  | "celebrating"
  | "camera-ready"
  | "sad"
  | "cross"
  // New from Character Bible v2: wide-eyed + pulled-back. Used when
  // something genuinely surprising happens (a giant unexpected payment,
  // a major mistake the studio made being detected by Nova, etc.). She
  // is shocked WITH you, not at you.
  | "shocked";

export interface NovaProps {
  /** Which expression to render. Default: `idle`. */
  state?: NovaState;
  /**
   * Pixel size (height). Width is computed from the 120 × 160 viewBox
   * (so width ≈ size × 0.75). Default: 88.
   *
   * Pass `"fill"` to let Nova fill her parent container instead —
   * required by NovaScene (#337) which sizes her via vmin on the wrapper.
   */
  size?: number | "fill";
  /** Extra classes for the wrapper. */
  className?: string;
  /** Accessible label. Default describes the state. */
  ariaLabel?: string;
  /** Disable animations (still picks the right pose). Default true. */
  animated?: boolean;
}

// Token shorthands, used inline below. Keeping them as CSS-var strings means
// Nova adapts to future themes without code changes.
const TEAL = "hsl(var(--lunar-teal))";
const TEAL_DEEP = "hsl(182 43% 22%)";
const IVORY = "hsl(var(--cosmic-ivory))";
const NEAR_BLACK = "hsl(36 10% 8%)";
const ORANGE = "hsl(var(--orbit-orange))";
const PEACH = "hsl(20 55% 72%)";
const MUSTARD = "hsl(var(--mustard))";
const SIGNAL_BLUE = "hsl(207 60% 78%)"; // for sad tear

export function Nova({
  state = "idle",
  size = 88,
  className,
  ariaLabel,
  animated = true,
}: NovaProps) {
  // #392 — For the 5 core moods NovaV2 supports, delegate to the new
  // Character Bible visual. Every consumer that renders <Nova state=…/>
  // automatically inherits the new look for those moods without any
  // call-site changes. Remaining states (sleepy, celebrating,
  // camera-ready, sad, cross, shocked) continue to render the legacy
  // SVG below until #393 ports them.
  const v2Moods: Partial<Record<NovaState, 'idle' | 'thinking' | 'curious' | 'happy' | 'has-suggestion'>> = {
    idle: 'idle',
    thinking: 'thinking',
    curious: 'curious',
    happy: 'happy',
    'has-suggestion': 'has-suggestion',
  };
  const v2Mood = v2Moods[state];
  if (v2Mood) {
    const px = size === 'fill' ? 96 : size;
    // Import lazily to avoid pulling ~90KB of nova-v2 JS on surfaces
    // that render Nova once at small size. LazyNovaV2 is the memoized
    // wrapper below.
    return (
      <LazyNovaV2
        mood={v2Mood}
        size={px}
        className={className}
        ariaLabel={ariaLabel ?? `Nova, ${state.replace('-', ' ')}`}
        fill={size === 'fill'}
      />
    );
  }

  const hasCheeks =
    state === "has-suggestion" ||
    state === "happy" ||
    state === "celebrating" ||
    state === "camera-ready" ||
    state === "sad";

  const headTilt = state === "curious" ? -10 : 0;
  const isThinking = state === "thinking";

  return (
    <div
      className={cn(
        "relative inline-flex items-end justify-center",
        animated && `nova-anim nova-state-${state}`,
        className,
      )}
      style={
        size === "fill"
          ? { width: "100%", height: "100%" }
          : { width: size * 0.75, height: size }
      }
      role="img"
      aria-label={ariaLabel ?? `Nova, ${state.replace("-", " ")}`}
    >
      <svg
        viewBox="0 0 120 160"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        className="overflow-visible"
      >
        <defs>
          {/* Vertical curvature gradient — lighter top, base middle, darker bottom. */}
          <linearGradient id="nova-teal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(182 50% 42%)" />
            <stop offset="55%" stopColor="hsl(182 43% 31%)" />
            <stop offset="100%" stopColor="hsl(182 50% 22%)" />
          </linearGradient>
          <linearGradient id="nova-teal-deep" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(182 50% 30%)" />
            <stop offset="100%" stopColor="hsl(182 50% 16%)" />
          </linearGradient>
          <linearGradient id="nova-screen-glass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.12" />
            <stop offset="50%" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="nova-orb" cx="35%" cy="30%" r="70%">
            <stop offset="0%" stopColor="hsl(20 90% 75%)" />
            <stop offset="55%" stopColor="hsl(var(--orbit-orange))" />
            <stop offset="100%" stopColor="hsl(15 80% 35%)" />
          </radialGradient>
          <radialGradient id="nova-cheek" cx="40%" cy="40%" r="60%">
            <stop offset="0%" stopColor="hsl(20 70% 82%)" />
            <stop offset="100%" stopColor="hsl(20 55% 64%)" stopOpacity="0.7" />
          </radialGradient>
          <filter id="nova-drop" x="-30%" y="-10%" width="160%" height="130%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2.5" />
            <feOffset dx="0" dy="3" result="off" />
            <feComponentTransfer><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Contact shadow — sits beneath Nova, outside the body group */}
        <ellipse className="nova-contact-shadow" cx="60" cy="153" rx="34" ry="3.5" fill="hsl(36 10% 8%)" opacity="0.18" />

        {/* Confetti — celebrating only, behind everything */}
        {state === "celebrating" && <Confetti />}

        {/* The figure as a whole — body-level animations (sway/float/bounce/drift) apply here */}
        <g className="nova-body-group" filter="url(#nova-drop)">
          {/* Antenna */}
          <g>
            <line
              x1="60"
              y1="32"
              x2="60"
              y2="14"
              stroke={NEAR_BLACK}
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <ellipse
              cx="60"
              cy="10"
              rx="7.5"
              ry="2.2"
              stroke={NEAR_BLACK}
              strokeWidth="1"
              fill="none"
            />
            {/* Orb — gets the antenna-ball class so per-state pulses can target it */}
            <circle className="nova-antenna-ball" cx="60" cy="10" r="3.5" fill="url(#nova-orb)" />
            <circle cx="58.5" cy="8.5" r="1.1" fill="white" opacity="0.7" pointerEvents="none" />
            {/* Sleepy: Z above the antenna */}
            {state === "sleepy" && <SleepyZees />}
            {/* Cross: stress mark above the antenna */}
            {state === "cross" && <StressMark />}
          </g>

          {/* Head + screen (rotates on `curious`).
              Matches iOS Nova.swift: outer darker frame + inner lighter
              panel reads as "device frame" rather than the previous
              "lighter teal with chin shelf" treatment. The 2px visible
              border between TEAL_DEEP outer and TEAL inner is what
              gives Nova her premium-electronics feel. */}
          <g
            className="nova-head"
            style={{
              transformOrigin: "60px 60px",
              transform: `rotate(${headTilt}deg)`,
              transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            {/* Outer darker frame — 80x60. Reads as the device chassis. */}
            <rect x="20" y="30" width="80" height="60" rx="14" fill="url(#nova-teal-deep)" />
            {/* Inner lighter panel — 76x56, sits inside the frame with a
                2px reveal so the frame line is visible all the way around. */}
            <rect x="22" y="32" width="76" height="56" rx="12" fill="url(#nova-teal)" />
            <path d="M 22 32 Q 60 30 98 32 L 96 42 Q 60 38 24 42 Z" fill="white" opacity="0.18" />
            {/* Screen — 68x42, larger than v1 to match Bible proportions
                where the screen is clearly the focal point of Nova's
                face. (Was 64x34 with too much teal bezel.) */}
            <rect x="26" y="40" width="68" height="42" rx="8" fill={NEAR_BLACK} />
            <rect x="26" y="40" width="68" height="42" rx="8" fill="url(#nova-screen-glass)" pointerEvents="none" />

            {/* Eyes — keyed on state so the cross-fade re-fires on change */}
            <g key={state} className="nova-eyes" style={{ animation: "nova-eye-fade 220ms ease-out" }}>
              <Eyes state={state} />
            </g>

            {/* Sad: a teardrop falling from the right eye */}
            {state === "sad" && (
              <ellipse
                className="nova-tear"
                cx="74"
                cy="74"
                rx="2.2"
                ry="3"
                fill={SIGNAL_BLUE}
              />
            )}
          </g>

          {/* Cheeks (subset of states) */}
          {hasCheeks && (
            <>
              <circle className="nova-cheek" cx="32" cy="86" r="3" fill={state === "sad" ? SIGNAL_BLUE : "url(#nova-cheek)"} opacity={0.9} />
              <circle className="nova-cheek" cx="88" cy="86" r="3" fill={state === "sad" ? SIGNAL_BLUE : "url(#nova-cheek)"} opacity={0.9} />
            </>
          )}

          {/* Body block */}
          <g>
            <rect x="14" y="96" width="92" height="50" rx="12" fill="url(#nova-teal)" />
            <rect x="22" y="104" width="76" height="34" rx="8" fill="url(#nova-teal-deep)" />
            <rect x="14" y="96" width="92" height="6" rx="4" fill="white" opacity="0.14" />
            <rect x="22" y="104" width="76" height="3" rx="2" fill={TEAL} opacity={0.5} />
          </g>

          {/* Arms (celebrating, camera-ready) */}
          {state === "celebrating" && <CelebratingArms />}
          {state === "camera-ready" && <CameraArm />}
        </g>
      </svg>

      {/* All keyframes scoped via .nova-anim. Each state gets its own scope
          so multiple Novas with different states on the same page don't fight. */}
      <style>{`
        @keyframes nova-eye-fade {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .nova-anim.nova-state-idle .nova-contact-shadow,
        .nova-anim.nova-state-happy .nova-contact-shadow,
        .nova-anim.nova-state-celebrating .nova-contact-shadow {
          animation: nova-shadow-breathe 3s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: 60px 153px;
        }
        @keyframes nova-shadow-breathe {
          0%, 100% { transform: scaleX(1); opacity: 0.18; }
          50%      { transform: scaleX(0.85); opacity: 0.10; }
        }

        /* IDLE — gentle float + occasional blink */
        @keyframes nova-idle-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-6px); }
        }
        @keyframes nova-blink {
          0%, 92%, 100% { transform: scaleY(1); }
          94%, 96.5%    { transform: scaleY(0.15); }
          98%           { transform: scaleY(1); }
        }
        .nova-anim.nova-state-idle .nova-body-group {
          animation: nova-idle-float 3s cubic-bezier(0.4,0,0.2,1) infinite;
        }
        .nova-anim.nova-state-idle .nova-eyes {
          animation: nova-blink 4s cubic-bezier(0.4,0,0.2,1) infinite;
          animation-delay: 0.3s;
          transform-origin: center;
        }

        /* CURIOUS — head-tilt back-and-forth + blink */
        @keyframes nova-curious-tilt {
          0%, 100% { transform: rotate(-2deg); }
          50%      { transform: rotate(8deg); }
        }
        .nova-anim.nova-state-curious .nova-head {
          animation: nova-curious-tilt 2s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: 60px 60px;
        }
        .nova-anim.nova-state-curious .nova-eyes {
          animation: nova-blink 4s cubic-bezier(0.4,0,0.2,1) infinite;
          animation-delay: 1.4s;
          transform-origin: center;
        }

        /* HAS-SUGGESTION — bouncy + ball pulse */
        @keyframes nova-suggestion-bounce {
          0%, 100% { transform: translateY(0); }
          35%      { transform: translateY(-10px); }
          55%      { transform: translateY(-2px); }
          75%      { transform: translateY(-6px); }
        }
        @keyframes nova-suggestion-ball {
          0%, 100% { fill: ${ORANGE}; }
          35%      { fill: #F08050; }
          55%      { fill: ${ORANGE}; }
          75%      { fill: #F08050; }
        }
        .nova-anim.nova-state-has-suggestion .nova-body-group {
          animation: nova-suggestion-bounce 1.2s cubic-bezier(0.34,1.56,0.64,1) infinite;
        }
        .nova-anim.nova-state-has-suggestion .nova-antenna-ball {
          animation: nova-suggestion-ball 1.2s cubic-bezier(0.4,0,0.2,1) infinite;
        }
        .nova-anim.nova-state-has-suggestion .nova-eyes {
          animation: nova-blink 4s cubic-bezier(0.4,0,0.2,1) infinite;
          animation-delay: 0.9s;
          transform-origin: center;
        }

        /* HAPPY — gentle sway + cheek pulse */
        @keyframes nova-happy-sway {
          0%, 100% { transform: rotate(-4deg); }
          50%      { transform: rotate(4deg); }
        }
        @keyframes nova-happy-cheek {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 0.9; }
        }
        .nova-anim.nova-state-happy .nova-body-group {
          animation: nova-happy-sway 2s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: 60px 100px;
        }
        .nova-anim.nova-state-happy .nova-cheek {
          animation: nova-happy-cheek 2s cubic-bezier(0.4,0,0.2,1) infinite;
        }

        /* SLEEPY — slow drift, head tilted permanently */
        @keyframes nova-sleepy-drift {
          0%, 100% { transform: translateY(0) rotate(-3deg); }
          50%      { transform: translateY(-10px) rotate(-3deg); }
        }
        @keyframes nova-sleepy-zee {
          0%   { transform: translate(0, 0) scale(0.7); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate(8px, -22px) scale(1.1); opacity: 0; }
        }
        .nova-anim.nova-state-sleepy .nova-body-group {
          animation: nova-sleepy-drift 5s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: center bottom;
        }
        .nova-anim.nova-state-sleepy .nova-zee {
          animation: nova-sleepy-zee 3s ease-out infinite;
        }
        .nova-anim.nova-state-sleepy .nova-zee-2 { animation-delay: 1.2s; }

        /* CELEBRATING — bounce + confetti rain */
        @keyframes nova-celebrating-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-12px); }
        }
        @keyframes nova-confetti-fall {
          0%   { transform: translateY(-30px) rotate(0deg); opacity: 0; }
          8%   { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(220px) rotate(360deg); opacity: 0; }
        }
        .nova-anim.nova-state-celebrating .nova-body-group {
          animation: nova-celebrating-bounce 0.8s cubic-bezier(0.34,1.56,0.64,1) infinite;
        }
        .nova-anim.nova-state-celebrating .nova-eyes {
          animation: nova-blink 4s cubic-bezier(0.4,0,0.2,1) infinite;
          animation-delay: 1.7s;
          transform-origin: center;
        }
        .nova-anim.nova-state-celebrating .nova-confetti {
          transform-origin: center;
          animation: nova-confetti-fall 2.5s linear infinite;
        }
        .nova-anim.nova-state-celebrating .nova-c1  { animation-delay: 0s;    }
        .nova-anim.nova-state-celebrating .nova-c2  { animation-delay: 0.3s;  }
        .nova-anim.nova-state-celebrating .nova-c3  { animation-delay: 0.6s;  }
        .nova-anim.nova-state-celebrating .nova-c4  { animation-delay: 0.9s;  }
        .nova-anim.nova-state-celebrating .nova-c5  { animation-delay: 1.2s;  }
        .nova-anim.nova-state-celebrating .nova-c6  { animation-delay: 1.5s;  }
        .nova-anim.nova-state-celebrating .nova-c7  { animation-delay: 1.8s;  }
        .nova-anim.nova-state-celebrating .nova-c8  { animation-delay: 0.45s; }

        /* CAMERA-READY — subtle hover, blinks */
        @keyframes nova-camera-hover {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50%      { transform: translateY(-4px) rotate(-2deg); }
        }
        .nova-anim.nova-state-camera-ready .nova-body-group {
          animation: nova-camera-hover 4s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: center bottom;
        }
        .nova-anim.nova-state-camera-ready .nova-eyes {
          animation: nova-blink 4s cubic-bezier(0.4,0,0.2,1) infinite;
          animation-delay: 0.7s;
          transform-origin: center;
        }

        /* SAD — slow droop drift, tear falls */
        @keyframes nova-sad-drift {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(4px) rotate(-2deg); }
        }
        @keyframes nova-sad-tear {
          0%   { transform: translateY(0) scaleY(0.8); opacity: 0; }
          15%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(40px) scaleY(1.2); opacity: 0; }
        }
        .nova-anim.nova-state-sad .nova-body-group {
          animation: nova-sad-drift 5s cubic-bezier(0.55,0.06,0.68,0.19) infinite;
          transform-origin: center bottom;
        }
        .nova-anim.nova-state-sad .nova-tear {
          transform-origin: center;
          animation: nova-sad-tear 4s cubic-bezier(0.55,0.06,0.68,0.19) infinite;
          animation-delay: 0.6s;
        }

        /* CROSS — backward lean + tension jitter + huff */
        @keyframes nova-cross-body {
          0%   { transform: rotate(-8deg) translate(0, 0); }
          2.5% { transform: rotate(-8deg) translate(-2px, 0); }
          5%   { transform: rotate(-8deg) translate(0, 0); }
          7.5% { transform: rotate(-8deg) translate(2px, 0); }
          10%  { transform: rotate(-8deg) translate(0, 0); }
          20%  { transform: rotate(-8deg) translate(-2px, 0); }
          30%  { transform: rotate(-8deg) translate(2px, 0); }
          40%  { transform: rotate(-8deg) translate(-2px, 0); }
          50%  { transform: rotate(-8deg) translate(2px, 0); }
          60%  { transform: rotate(-8deg) translate(-2px, 0); }
          70%  { transform: rotate(-8deg) translate(2px, 0); }
          80%  { transform: rotate(-8deg) translate(-2px, 0); }
          90%  { transform: rotate(-8deg) translate(0, 0); }
          94%  { transform: rotate(-8deg) translate(0, -4px); }
          97%  { transform: rotate(-8deg) translate(0, 0); }
          100% { transform: rotate(-8deg) translate(0, 0); }
        }
        @keyframes nova-cross-heat {
          0%, 85%, 100% { fill: ${ORANGE}; }
          90%, 95%      { fill: #E84545; }
        }
        @keyframes nova-cross-stress {
          0%, 30%   { opacity: 1; }
          30.01%, 70%   { opacity: 0; }
          70.01%, 100% { opacity: 1; }
        }
        .nova-anim.nova-state-cross .nova-body-group {
          animation: nova-cross-body 3s cubic-bezier(0.4,0,0.2,1) infinite;
          transform-origin: center bottom;
        }
        .nova-anim.nova-state-cross .nova-antenna-ball {
          animation: nova-cross-heat 2s linear infinite;
        }
        .nova-anim.nova-state-cross .nova-stress-mark {
          animation: nova-cross-stress 2s steps(1, end) infinite;
        }

        /* SHOCKED — sharp pull-back + recovery, then gentle settle.
           Mirrors the "did NOT see that coming" beat described in the
           Character Bible. Body lurches back briefly on each cycle,
           eyes pop wider on the same beat, then everything relaxes. */
        @keyframes nova-shocked-body {
          0%   { transform: translate(0, 0) rotate(0deg); }
          8%   { transform: translate(0, -8px) rotate(-3deg); }
          20%  { transform: translate(0, -2px) rotate(-1deg); }
          40%  { transform: translate(0, 0) rotate(0deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        @keyframes nova-shocked-eye-pop {
          0%   { transform: scale(0.85); }
          8%   { transform: scale(1.18); }
          20%  { transform: scale(1.0); }
          100% { transform: scale(1.0); }
        }
        @keyframes nova-shocked-ball {
          0%, 100% { fill: ${ORANGE}; }
          8%       { fill: ${MUSTARD}; }
          30%      { fill: ${ORANGE}; }
        }
        .nova-anim.nova-state-shocked .nova-body-group {
          animation: nova-shocked-body 3.6s cubic-bezier(0.34,1.56,0.64,1) infinite;
          transform-origin: center bottom;
        }
        .nova-anim.nova-state-shocked .nova-eyes {
          animation: nova-shocked-eye-pop 3.6s cubic-bezier(0.34,1.56,0.64,1) infinite;
          transform-origin: center;
          transform-box: fill-box;
        }
        .nova-anim.nova-state-shocked .nova-antenna-ball {
          animation: nova-shocked-ball 3.6s cubic-bezier(0.4,0,0.2,1) infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .nova-anim .nova-body-group,
          .nova-anim .nova-eyes,
          .nova-anim .nova-cheek,
          .nova-anim .nova-antenna-ball,
          .nova-anim .nova-confetti,
          .nova-anim .nova-tear,
          .nova-anim .nova-stress-mark,
          .nova-anim .nova-zee {
            animation-duration: 8s !important;
          }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Eye variants — one per state. Coords are inside the screen rect (28..92 × 42..76).
// ─────────────────────────────────────────────────────────────────────────────

function Eyes({ state }: { state: NovaState }) {
  // Eye centers — moved slightly outward and up from v1 to match
  // Bible proportions on the larger 68x42 screen. Was lx=46/rx=74/cy=59
  // when the screen was 64x34 starting at y=42. Now the screen is
  // 68x42 starting at y=40, so eye y centers shift to 60 and the
  // outer spacing widens slightly.
  const lx = 44;
  const rx = 76;
  const cy = 60;

  switch (state) {
    case "thinking":
      return (
        <>
          <ThinkingPyramid cx={lx} cy={cy} />
          <ThinkingPyramid cx={rx} cy={cy} />
        </>
      );

    case "happy":
      return (
        <>
          <path d={`M ${lx - 6} ${cy + 3} Q ${lx} ${cy - 5} ${lx + 6} ${cy + 3}`}
            stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" />
          <path d={`M ${rx - 6} ${cy + 3} Q ${rx} ${cy - 5} ${rx + 6} ${cy + 3}`}
            stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" />
        </>
      );

    case "sleepy":
      return (
        <>
          <rect x={lx - 6} y={cy - 1.2} width="12" height="2.4" rx="1.2" fill={SIGNAL_BLUE} />
          <rect x={rx - 6} y={cy - 1.2} width="12" height="2.4" rx="1.2" fill={SIGNAL_BLUE} />
        </>
      );

    case "sad":
      // Inverted (sad-arc) eyes — open at the top, like a frown turned to eyes
      return (
        <>
          <path d={`M ${lx - 6} ${cy - 3} Q ${lx} ${cy + 5} ${lx + 6} ${cy - 3}`}
            stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.9" />
          <path d={`M ${rx - 6} ${cy - 3} Q ${rx} ${cy + 5} ${rx + 6} ${cy - 3}`}
            stroke={SIGNAL_BLUE} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.9" />
        </>
      );

    case "cross":
      // Sharp tilted triangles — angry V brows merged with eyes.
      // Stays orange because Cross is the one state where rage reads
      // through hue, not just shape.
      return (
        <>
          <Triangle cx={lx} cy={cy} color={ORANGE} tilt={25} />
          <Triangle cx={rx} cy={cy} color={ORANGE} tilt={-25} />
        </>
      );

    case "shocked":
      // Wide-open round eyes with dark pupils — pulled-back "did
      // NOT see that coming" energy. Outer ring is the open eye, inner
      // dot reads as a pupil. Different shape from every other state
      // so the surprise reads instantly.
      return (
        <>
          <circle cx={lx} cy={cy} r="7" fill={SIGNAL_BLUE} />
          <circle cx={rx} cy={cy} r="7" fill={SIGNAL_BLUE} />
          <circle cx={lx} cy={cy} r="2.4" fill={NEAR_BLACK} />
          <circle cx={rx} cy={cy} r="2.4" fill={NEAR_BLACK} />
        </>
      );

    case "idle":
    case "curious":
    case "has-suggestion":
    case "celebrating":
    case "camera-ready":
    default:
      // Default eye color is signature signal-blue per Character Bible
      // (was ivory in v1 — that was the visual mismatch).
      return (
        <>
          <Triangle cx={lx} cy={cy} color={SIGNAL_BLUE} />
          <Triangle cx={rx} cy={cy} color={SIGNAL_BLUE} />
        </>
      );
  }
}

function Triangle({
  cx,
  cy,
  color,
  flipped = false,
  tilt = 0,
}: {
  cx: number;
  cy: number;
  color: string;
  flipped?: boolean;
  tilt?: number;
}) {
  // Bigger triangles — Bible's eyes read large and confident on the
  // screen, not as small specks. Was 5x6, now 7x9.
  const w = 7;
  const h = 9;
  const points = flipped
    ? `${cx - w},${cy - h} ${cx + w},${cy - h} ${cx},${cy + h}`
    : `${cx - w},${cy + h} ${cx + w},${cy + h} ${cx},${cy - h}`;
  return (
    <polygon
      points={points}
      fill={color}
      style={tilt ? { transformOrigin: `${cx}px ${cy}px`, transform: `rotate(${tilt}deg)` } : undefined}
    />
  );
}

/**
 * 6-block ROYGBV pyramid centered on (cx, cy), building bottom-up.
 * Layout (rows of 3, 2, 1):
 *   row0 (bottom): R O Y
 *   row1 (middle): G B
 *   row2 (top):    V
 */
function ThinkingPyramid({ cx, cy }: { cx: number; cy: number }) {
  const s = 3.2; // block size
  const gap = 0.6;
  const step = s + gap;
  // Bottom row sits below cy; pyramid grows upward.
  const baseY = cy + step; // y of top of bottom row
  const blocks = [
    // index, col, row, color
    { i: 0, col: -1, row: 0, c: "hsl(0 85% 60%)" },     // R
    { i: 1, col: 0,  row: 0, c: "hsl(28 95% 58%)" },    // O
    { i: 2, col: 1,  row: 0, c: "hsl(48 95% 58%)" },    // Y
    { i: 3, col: -0.5, row: 1, c: "hsl(140 65% 50%)" }, // G
    { i: 4, col: 0.5,  row: 1, c: "hsl(210 85% 58%)" }, // B
    { i: 5, col: 0,    row: 2, c: "hsl(280 65% 60%)" }, // V
  ];
  return (
    <g>
      {blocks.map((b) => (
        <rect
          key={b.i}
          className={`thinking-block-${b.i}`}
          x={cx + b.col * step - s / 2}
          y={baseY - b.row * step - s}
          width={s}
          height={s}
          rx={0.6}
          fill={b.c}
        />
      ))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Decorations
// ─────────────────────────────────────────────────────────────────────────────

function CelebratingArms() {
  return (
    <g stroke={NEAR_BLACK} strokeWidth="2.5" strokeLinecap="round" fill="none">
      <path d="M 18 108 Q 6 100 4 86" />
      <path d="M 102 108 Q 114 100 116 86" />
      <circle cx="4" cy="84" r="2.5" fill={NEAR_BLACK} />
      <circle cx="116" cy="84" r="2.5" fill={NEAR_BLACK} />
    </g>
  );
}

function CameraArm() {
  return (
    <g>
      <path d="M 102 116 Q 112 122 116 132" stroke={NEAR_BLACK} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <rect x="108" y="130" width="14" height="10" rx="2" fill={NEAR_BLACK} />
      <circle cx="115" cy="135" r="3" fill={IVORY} />
      <circle cx="115" cy="135" r="1.5" fill={NEAR_BLACK} />
      <circle cx="120" cy="131.5" r="0.8" fill={ORANGE} />
    </g>
  );
}

function Confetti() {
  // 8 small mustard/orange/teal squares falling and rotating
  const colors = [MUSTARD, ORANGE, TEAL, MUSTARD, ORANGE, TEAL, MUSTARD, ORANGE];
  const xs = [22, 38, 52, 66, 80, 94, 30, 88];
  return (
    <g>
      {colors.map((c, i) => (
        <rect
          key={i}
          className={`nova-confetti nova-c${i + 1}`}
          x={xs[i]}
          y={-10}
          width={3.5}
          height={3.5}
          rx={0.8}
          fill={c}
          style={{ transformOrigin: `${xs[i] + 1.75}px -8.25px` }}
        />
      ))}
    </g>
  );
}

function SleepyZees() {
  return (
    <g fill={NEAR_BLACK} fontFamily="ui-monospace, monospace" fontSize="10" fontWeight="700">
      <text className="nova-zee" x="72" y="14">z</text>
      <text className="nova-zee nova-zee-2" x="78" y="6">z</text>
    </g>
  );
}

function StressMark() {
  // Cartoon "anger" lines — three short ticks above the antenna ring
  return (
    <g className="nova-stress-mark" stroke="#E84545" strokeWidth="1.6" strokeLinecap="round" fill="none">
      <line x1="48" y1="6" x2="44" y2="2" />
      <line x1="60" y1="2" x2="60" y2="-3" />
      <line x1="72" y1="6" x2="76" y2="2" />
    </g>
  );
}

export default Nova;

// ─── LazyNovaV2 wrapper ───────────────────────────────────────────
//
// #392 — Bridges the legacy `<Nova state size />` API to the new
// Character Bible visual (NovaV2). Kept in this file so it can share
// the same className / aria conventions the rest of the codebase
// expects. The actual heavy imports live inside NovaV2 (SVG builder
// + keyframe CSS) and only load when this branch fires.
import { NovaV2, type NovaV2Mood } from './NovaV2';

interface LazyNovaV2Props {
  mood: NovaV2Mood;
  size: number;
  className?: string;
  ariaLabel?: string;
  fill?: boolean;
}

function LazyNovaV2({
  mood,
  size,
  className,
  ariaLabel,
  fill,
}: LazyNovaV2Props) {
  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      role="img"
      aria-label={ariaLabel}
      style={
        fill
          ? { width: '100%', height: '100%' }
          : { width: size, height: size }
      }
    >
      <NovaV2 mood={mood} size={fill ? size : size} />
    </div>
  );
}
